import React from 'react';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  styled,
  alpha,
} from '@mui/material';
import {
  Tour as TourIcon,
  Description as DocumentationIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(17, 17, 17, 0.95)' 
      : 'rgba(248, 250, 252, 0.95)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${theme.customPalette?.border?.subtle || alpha(theme.palette.divider, 0.1)}`,
    borderRadius: '12px',
    minWidth: '280px',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 8px 32px rgba(0, 0, 0, 0.1)',
    padding: '8px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 16px',
  margin: '2px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(2px)',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const TourIconContainer = styled(IconContainer)(({ theme }) => ({
  backgroundColor: alpha('#FF6B35', 0.1),
  color: '#FF6B35',
}));

const DocumentationIconContainer = styled(IconContainer)(({ theme }) => ({
  backgroundColor: alpha('#4F46E5', 0.1),
  color: '#4F46E5',
}));

const SupportIconContainer = styled(IconContainer)(({ theme }) => ({
  backgroundColor: alpha('#10B981', 0.1),
  color: '#10B981',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: '16px 16px 8px 16px',
  borderBottom: `1px solid ${theme.customPalette?.border?.subtle || alpha(theme.palette.divider, 0.1)}`,
  marginBottom: '8px',
}));

interface HelpMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onTakeTour: () => void;
  onDocumentation: () => void;
  onContactSupport: () => void;
}

const HelpMenu: React.FC<HelpMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onTakeTour,
  onDocumentation,
  onContactSupport,
}) => {
  const theme = useTheme();

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <HeaderSection>
        <Typography
          variant="h6"
          sx={{
            color: '#FF6B35',
            fontWeight: 600,
            fontSize: '1.1rem',
            mb: 0.5,
          }}
        >
          Need Help?
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
          }}
        >
          Get assistance and learn more
        </Typography>
      </HeaderSection>

      <StyledMenuItem
        onClick={() => {
          onTakeTour();
          onClose();
        }}
      >
        <TourIconContainer>
          <TourIcon />
        </TourIconContainer>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              fontSize: '0.95rem',
            }}
          >
            Take the Tour
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8rem',
              mt: 0.25,
            }}
          >
            Learn how to use OpenANA Security
          </Typography>
        </Box>
      </StyledMenuItem>

      <StyledMenuItem
        onClick={() => {
          onDocumentation();
          onClose();
        }}
      >
        <DocumentationIconContainer>
          <DocumentationIcon />
        </DocumentationIconContainer>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              fontSize: '0.95rem',
            }}
          >
            Documentation
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8rem',
              mt: 0.25,
            }}
          >
            Detailed guides and references
          </Typography>
        </Box>
      </StyledMenuItem>

      <StyledMenuItem
        onClick={() => {
          onContactSupport();
          onClose();
        }}
      >
        <SupportIconContainer>
          <SupportIcon />
        </SupportIconContainer>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              fontSize: '0.95rem',
            }}
          >
            Contact Support
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8rem',
              mt: 0.25,
            }}
          >
            Get help from our team
          </Typography>
        </Box>
      </StyledMenuItem>
    </StyledMenu>
  );
};

export default HelpMenu;
